import * as THREE from 'three';
import {
  GLTFLoader,
  type GLTF,
} from 'three/examples/jsm/loaders/GLTFLoader.js';

import type { CharacterMeshFormat } from '@cartwhl/3d/player';
import type { MotionDownloadType } from '@cartwhl/common/api/getMotion';

import {
  coerceOpaqueBlendMaterials,
  stripMorphTargetsFromScene,
} from '../../lib/assetHygiene';
import { disposeCartographerObject3D } from '../../lib/disposeCartographerObject3D';
import { inferMeshFormatFromPath } from './motionExport';

export type CartographerMotionPreviewAssetKind = 'bvh' | 'gltf';

export interface CartographerMotionPreviewGltfAsset {
  clip: THREE.AnimationClip;
  scene: THREE.Group;
  bones: THREE.Bone[];
}

const MOTION_PREVIEW_DISPOSED_FLAG = 'cartographerMotionPreviewDisposed';

function inferMeshFormatFromFileName(
  fileName: string | null | undefined,
): CharacterMeshFormat | undefined {
  if (!fileName) return undefined;
  const lower = fileName.toLowerCase();
  if (lower.endsWith('.fbx')) return 'fbx';
  if (lower.endsWith('.glb')) return 'glb';
  if (lower.endsWith('.gltf')) return 'gltf';
  return undefined;
}

function prepareMotionPreviewGltfScene(scene: THREE.Group) {
  coerceOpaqueBlendMaterials(scene);
  stripMorphTargetsFromScene(scene);

  scene.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;
    object.castShadow = true;
    object.receiveShadow = true;
    object.frustumCulled = false;
  });

  scene.updateMatrixWorld(true);
}

export function resolveMotionPreviewAssetKind(options: {
  meshFormat?: CharacterMeshFormat | null;
  meshPath?: string | null;
  fileName?: string | null;
}): CartographerMotionPreviewAssetKind {
  const resolvedMeshFormat =
    options.meshFormat ??
    (options.meshPath ? inferMeshFormatFromPath(options.meshPath) : undefined) ??
    inferMeshFormatFromFileName(options.fileName);

  return resolvedMeshFormat === 'fbx' ? 'bvh' : 'gltf';
}

export function resolveMotionPreviewDownloadType(
  assetKind: CartographerMotionPreviewAssetKind,
): MotionDownloadType {
  return assetKind === 'gltf' ? 'gltf' : 'bvh';
}

export function disposeMotionPreviewGltfAsset(
  asset: CartographerMotionPreviewGltfAsset | null,
) {
  if (!asset) return;
  if (asset.scene.userData[MOTION_PREVIEW_DISPOSED_FLAG]) return;

  asset.scene.userData[MOTION_PREVIEW_DISPOSED_FLAG] = true;
  disposeCartographerObject3D(asset.scene);
}

export function selectMotionPreviewGltfClip(
  animations: THREE.AnimationClip[],
): THREE.AnimationClip | null {
  const playableAnimations = animations.filter(
    (animation) => animation.duration > 0 && animation.tracks.length > 0,
  );
  if (playableAnimations.length === 0) return null;

  // Exports can include a tiny setup/T-pose clip alongside the real motion.
  // Prefer the richest non-tpose clip so all preview surfaces pick the same one.
  return playableAnimations.reduce((best, current) => {
    const bestIsTpose = /^t[-_ ]?pose$/i.test(best.name);
    const currentIsTpose = /^t[-_ ]?pose$/i.test(current.name);
    if (bestIsTpose !== currentIsTpose) {
      return currentIsTpose ? best : current;
    }
    return current.tracks.length > best.tracks.length ? current : best;
  });
}

export async function loadMotionPreviewGltfAsset(
  gltfUrl: string,
): Promise<CartographerMotionPreviewGltfAsset | null> {
  const loader = new GLTFLoader();
  loader.setCrossOrigin('anonymous');
  const gltf = await new Promise<GLTF>((resolve, reject) => {
    loader.load(gltfUrl, resolve, undefined, reject);
  });

  const clip = selectMotionPreviewGltfClip(gltf.animations);
  if (!clip) return null;

  const scene = gltf.scene;
  prepareMotionPreviewGltfScene(scene);

  const bones: THREE.Bone[] = [];
  scene.traverse((object) => {
    if (object instanceof THREE.Bone) {
      bones.push(object);
    }
  });

  return {
    clip,
    scene,
    bones,
  };
}
